import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  Grid,
  Box,
  useTheme,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  PersonAdd,
  ArrowBack,
  ArrowForward,
} from "@mui/icons-material";
import { PageContainer, Breadcrumb } from "../common";
import Loading from "../common/Loading";
import { client } from "src/common/axiosClient";
import moment from "moment";

export default function UserList() {
  const theme = useTheme();
  const borderColor = theme.palette.divider;

  const [isLoading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(10);
  const [totalPage, setTotalPage] = useState<number>(1);
  const [isRegisterOpen, setRegisterOpen] = useState<boolean>(false);
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [name, setName] = useState<string>("");

  useEffect(() => {
    getUserList();
  }, [currentPage, perPage]);

  const getUserList = async () => {
    setLoading(true);
    const response = await client.get(
      `admin/api/v1/auth/admin/user/all?page=${currentPage}&perPage=${perPage}`
    );
    if (response.status === 200) {
      setData(response.data.content);
      setTotalPage(response.data.paging.totalPage);
    } else {
      console.error(response);
    }
    setLoading(false);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleRegisterOpen = () => {
    setRegisterOpen(true);
  };

  const handleRegisterClose = () => {
    setRegisterOpen(false);
    setUsername("");
    setPassword("");
    setConfirmPassword("");
    setName("");
  };

  const handleRegister = async () => {
    if (password !== confirmPassword) {
      alert("비밀번호가 일치하지 않습니다.");
      return;
    }

    const userData = {
      username,
      password,
      name,
    };

    try {
      const response = await client.post(
        "admin/api/v1/auth/register",
        userData,
        {
          headers: { "Content-Type": "application/json" },
        }
      );
      if (response.status === 201) {
        alert("사용자가 성공적으로 등록되었습니다.");
        handleRegisterClose();
        getUserList(); // Refresh the user list
      }
    } catch (error) {
      console.error("사용자 등록 중 오류가 발생했습니다: ", error);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const response = await client.delete(
        `admin/api/v1/auth/admin/user/${id}`
      );
      if (response.status === 200) {
        alert("사용자가 성공적으로 삭제되었습니다.");
        getUserList(); // Refresh the user list
      }
    } catch (error) {
      console.error("사용자 삭제 중 오류가 발생했습니다: ", error);
    }
  };

  return (
    <PageContainer>
      <Loading isLoading={isLoading} />
      <Breadcrumb
        action={
          <div className="space-x-5">
            <Button
              variant="contained"
              size="small"
              startIcon={<PersonAdd />}
              onClick={handleRegisterOpen}
            >
              사용자 추가
            </Button>
          </div>
        }
      >
        <div className="flex items-center">
          <Typography variant="h6">사용자 관리</Typography>
        </div>
      </Breadcrumb>
      <Card sx={{ padding: "10px", border: `1px solid ${borderColor}` }}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>No.</TableCell>
                <TableCell>아이디</TableCell>
                <TableCell>이름</TableCell>
                <TableCell>역할</TableCell>
                <TableCell>등록일</TableCell>
                <TableCell>삭제</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((user, index) => (
                <TableRow key={user.id}>
                  <TableCell>{user.id}</TableCell>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>{user.nickname}</TableCell>
                  <TableCell>{user.role}</TableCell>
                  <TableCell>
                    {moment(user.createdAt).format("YYYY-MM-DD")}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(user.id)}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box display="flex" justifyContent="center" mt={2}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            이전
          </Button>
          <Typography variant="body2" mx={2}>
            {currentPage} / {totalPage}
          </Typography>
          <Button
            variant="outlined"
            endIcon={<ArrowForward />}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPage}
          >
            다음
          </Button>
        </Box>
      </Card>

      <Dialog open={isRegisterOpen} onClose={handleRegisterClose}>
        <DialogTitle>사용자 등록</DialogTitle>
        <DialogContent>
          <DialogContentText>
            새로운 사용자를 등록하려면 아래 정보를 입력하세요.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="아이디"
            type="text"
            fullWidth
            variant="outlined"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <TextField
            margin="dense"
            label="비밀번호"
            type="password"
            fullWidth
            variant="outlined"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <TextField
            margin="dense"
            label="비밀번호 확인"
            type="password"
            fullWidth
            variant="outlined"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          <TextField
            margin="dense"
            label="이름"
            type="text"
            fullWidth
            variant="outlined"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRegisterClose} color="primary">
            취소
          </Button>
          <Button onClick={handleRegister} color="primary">
            등록
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
}
